import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../auth/useUser";
import { useGlobalRunStore, isTerminalRun, runKey } from "../hooks/useGlobalRunStore";
import { useProducts, productName } from "../hooks/useProducts";
import { titledRunEndLine } from "../lib/runNotify";

// RunToast (0027): the in-app, in-the-moment completion surface. Mounted at
// the App root; watches the global run store and pops a transient toast when
// a run reaches a terminal state (ready / failed), at most once per run and
// revival (0159: an entry reviveRun streams again announces its real end
// afresh). The toast offers a "view" action and auto-dismisses.
//
// 0159 F4: a terminal error reads its real outcome. Only a failed run says
// "failed"; a client give-up and a server-reported interruption read
// their ruled line, titled (titledRunEndLine).
//
// Complements the other completion surfaces: RunCompletionNotifier fires the
// OS notification + sound when the tab is HIDDEN; this toast is the FOCUSED-
// tab equivalent. The header RunMenu is the persistent after-the-fact place.

type ToastKind = "ready" | "failed";
// endLine: the titled ruled line of an error that is not a failure (null for
// a ready or a failed run).
type ToastData = { key: string; productId: string; kind: ToastKind; name: string; endLine: string | null };

const AUTO_DISMISS_MS = 8000;

export function RunToast() {
  const { data: user } = useUser();
  const runs = useGlobalRunStore((s) => s.runs);
  const firedRef = useRef<Set<string>>(new Set());
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const productsQ = useProducts();

  useEffect(() => {
    if (!user) return;
    for (const r of Object.values(runs)) {
      const phase = r.snapshot.phase;
      if (!isTerminalRun(r.snapshot)) continue;
      const key = `${runKey(r)}#${r.revival ?? 0}`;
      if (firedRef.current.has(key)) continue;
      firedRef.current.add(key);
      const name = productName(productsQ.data?.products, r.productId);
      setToasts((prev) => [
        ...prev,
        {
          key,
          productId: r.productId,
          kind: phase === "complete" ? "ready" : "failed",
          name,
          endLine: titledRunEndLine(name, r.snapshot),
        },
      ]);
    }
  }, [runs, user, productsQ.data]);

  if (toasts.length === 0) return null;

  const dismiss = (key: string) => setToasts((prev) => prev.filter((t) => t.key !== key));

  return (
    <div className="fixed bottom-4 right-4 z-30 flex flex-col gap-2">
      {toasts.map((t) => (
        <ToastItem key={t.key} toast={t} onDismiss={() => dismiss(t.key)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: ToastData; onDismiss: () => void }) {
  const navigate = useNavigate();
  useEffect(() => {
    const id = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ready = toast.kind === "ready";
  const view = () => {
    onDismiss();
    navigate(`/products/${toast.productId}`);
  };

  return (
    <div
      role="status"
      className={`flex items-center gap-3 rounded-lg border px-3.5 py-2.5 text-xs shadow-xl backdrop-blur-sm animate-slide-up ${
        ready
          ? "border-ok-solid/40 bg-ok-solid/10"
          : "border-danger-solid/50 bg-danger-solid/10"
      }`}
    >
      <span
        className={ready ? "text-ok" : "text-danger"}
        aria-hidden="true"
      >
        {ready ? "✓" : "!"}
      </span>
      {/* A ruled end line wraps rather than truncates: it is a sentence the
          founder must read whole. The title is clamped; the state never is (a long
          title cannot hide "is ready" or "failed"). */}
      {!ready && toast.endLine ? (
        <span className="min-w-0 max-w-[16rem] text-ink-100 break-words">{toast.endLine}</span>
      ) : (
        <span className="flex min-w-0 max-w-[16rem] items-baseline gap-1 text-ink-100">
          <span className="min-w-0 truncate font-medium">“{toast.name}”</span>{" "}
          <span className="shrink-0">{ready ? "is ready" : "failed"}</span>
        </span>
      )}
      <button
        type="button"
        onClick={view}
        className="shrink-0 font-mono text-[11px] uppercase tracking-widest text-ink-200 hover:text-ink-50"
      >
        view →
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="shrink-0 text-ink-500 hover:text-ink-300"
      >
        ✕
      </button>
    </div>
  );
}
